<template>
  <PageLayoutWrapper
    page="courses/_id"
    :props="{...$props, course }"
  >
    <article ref="content">
      <h1 class="pb-2 mb-0">{{ courseDisplayHeading }}</h1>
      <PageTemplateRenderer
        :content="course"
        type="course"
      />
    </article>
  </PageLayoutWrapper>
</template>

<script>
import { mapGetters } from 'vuex'
import _uniq from 'lodash/uniq'
import PageLayoutWrapper from '@/components/PageLayoutWrapper'
import pageLayout from '@/mixins/pageLayoutMixin'
import PageTemplateRenderer from '@/components/PageTemplateRenderer'
import CoursesService from '@/services/courses'
import { generateCourseLabel } from '@/utils/dataLabels'

export default {
  components: {
    PageLayoutWrapper,
    PageTemplateRenderer,
  },
  mixins: [pageLayout],
  head() {
    return {
      title: `${this.course.code} Course | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.course.code} Course | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('settings', ['catalogWideMetaTitle']),
    courseDisplayHeading() {
      return generateCourseLabel(this.course)
    },
  },
  async asyncData({ params, $axios, $bugsnag, redirect, store }) {
    const coursePageTemplate = store.state.settings.coursePageTemplate
    const courseQuestions = Object.values(coursePageTemplate.questions)
    const baseQuestions = [
      'code',
      'name',
      'subjectCode',
      'courseNumber',
      'longName',
    ]
    const courseQuestionsDataKeys = _uniq(
      courseQuestions
        .map((q) => {
          if (q.custom) return `customFields.${q.dataKey}`
          else return q.dataKey
        })
        .concat(baseQuestions)
    )

    const hasDependenciesField = !!Object.values(
      coursePageTemplate.questions
    ).find((field) => field.questionId === 'dependencies')
    const hasCrossListedCoursesField = !!Object.values(
      coursePageTemplate.questions
    ).find((field) => field.questionId === 'crossListedCourses')

    const api = new CoursesService($axios, store)

    try {
      const [course, { terms }, { semesterNames }] = await Promise.all([
        api.getCourse(params.id, {
          columns: courseQuestionsDataKeys,
          formatDependents: hasDependenciesField,
          includeCrosslisted: hasCrossListedCoursesField,
        }),
        $axios.$get(`/${store.state.settings.school}/general/terms`),
        $axios.$get(`/${store.state.settings.school}/general/semesterNames`),
      ])

      if (!course) {
        redirect('/404')
        return
      }

      return {
        course: {
          ...(course.customFields || {}),
          ...course,
        },
        terms,
        semesterNames,
      }
    } catch (e) {
      console.error(e)
      $bugsnag.notify(e)
      redirect('/404')
    }
  },
  created() {
    this.$store.commit('page/SET_CURRENT_PAGE_LABEL', this.courseDisplayHeading)
  },
  provide() {
    return {
      terms: this.terms,
      semesterNames: this.semesterNames,
    }
  },
}
</script>

<style lang="css" scoped>
.nuxt-link-active {
  @apply border-l border-t border-r rounded-t;
}
</style>
